import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import type { Store } from '@warp-drive/core';
import { reloadRecord } from './save-record.ts';
import type { MatchParticipantInsights } from '../types/secure-your-future';
import type { MatchParticipantExtension as MatchParticipant } from '../data/resources/match-participant.ext.ts';

const matchParticipantsUrl = (path: string) => {
  const opts: QueryUrlOptions = {
    identifier: { type: 'match-participants' },
    op: 'query',
    resourcePath: 'match-participants',
  };
  return `${buildBaseURL(opts)}/${path.replace(/^\/+/, '')}`;
};

/** PATCH /match-participants/:id/begin-enrollment */
export function beginEnrollmentRequest(id: string): RequestInfo {
  return {
    url: matchParticipantsUrl(`${id}/begin-enrollment`),
    method: 'PATCH',
    cacheOptions: { [SkipCache]: true },
  };
}

/** PATCH /match-participants/:id/attempt-complete-enrollment */
export function activateMatchParticipantRequest(id: string): RequestInfo {
  return {
    url: matchParticipantsUrl(`${id}/attempt-complete-enrollment`),
    method: 'PATCH',
    cacheOptions: { [SkipCache]: true },
  };
}

/** GET /match-participants/:id/insights */
export function getMatchParticipantInsightsRequest(id: string): RequestInfo {
  return {
    url: matchParticipantsUrl(`${id}/insights`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Begin enrollment for a match participant and refresh the record.
 *
 * Replaces the legacy `matchParticipant.beginEnrollment()` method — WarpDrive
 * resource extensions can't participate in Ember DI (no owner on schema-record
 * proxies), so request-issuing helpers live here as free functions and take
 * `store` explicitly, matching `saveRecord` / `reloadRecord` in
 * `save-record.ts`.
 *
 * The PATCH endpoint returns `head 204` (api_controller.rb perform_action), so
 * the cache is NOT refreshed by the request itself — keep the reload via the
 * builder helper.
 */
export async function beginMatchParticipantEnrollment(
  store: Store,
  participant: MatchParticipant
): Promise<MatchParticipant> {
  await store.request(beginEnrollmentRequest(participant.id as string));
  return reloadRecord(store, participant);
}

/**
 * Activate (attempt-complete-enrollment) a match participant and refresh the
 * record. Renamed from the legacy `activateMatchParticipant()` method to avoid
 * collision with the `activateMatchParticipantRequest` builder; see
 * `beginMatchParticipantEnrollment` for the rationale.
 *
 * The PATCH endpoint returns `head 204`, so we reload via the builder helper.
 */
export async function activateMatchParticipant(
  store: Store,
  participant: MatchParticipant
): Promise<MatchParticipant> {
  await store.request(activateMatchParticipantRequest(participant.id as string));
  return reloadRecord(store, participant);
}

/**
 * Fetch the insights payload and assign it onto the participant record.
 * Replaces the legacy `matchParticipant.fetchInsights()` method; see
 * `beginMatchParticipantEnrollment` for the rationale.
 *
 * Early-exits (no request) when `participant.hasDataForInsights` is false,
 * matching the legacy behavior.
 */
export async function fetchMatchParticipantInsights(
  store: Store,
  participant: MatchParticipant
): Promise<void> {
  if (!participant.hasDataForInsights) {
    return;
  }
  const response = await store.request(
    getMatchParticipantInsightsRequest(participant.id as string)
  );
  participant.insights = (response.content as { data: MatchParticipantInsights }).data;
}
